import React, { useEffect, useState } from 'react'
import { IonContent, IonPage } from '@ionic/react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { ArrowLeft, Clock3, ExternalLink, MessageSquare, ShieldAlert } from 'lucide-react'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import SupportContactLinks from '../components/SupportContactLinks'
import { Input } from '../components/ui/input'
import {
  createSubscriptionRefundRequest,
  getSubscriptionRefundRequestEligibility,
  type SubscriptionRefundRequestEligibility,
  type SubscriptionRefundRequestStatus,
} from '../features/subscriptionRefundRequests'
import useAstroApiClient from '../hooks/useAstroApiClient'
import { usePublicLegalConfiguration } from '../platform/legalConfig'
import { getSafeLinkAttributes } from '../platform/safeLinks'

const SubscriptionRefundRequestPage: React.FC = () => {
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const auth = useAuth()
  const api = useAstroApiClient()
  const publicLegalConfiguration = usePublicLegalConfiguration()

  const [eligibility, setEligibility] = useState<SubscriptionRefundRequestEligibility | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [contactEmail, setContactEmail] = useState('')
  const [reason, setReason] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let active = true

    const preferredEmail = typeof auth.user?.profile?.email === 'string'
      ? auth.user.profile.email
      : ''

    if (preferredEmail) {
      setContactEmail(preferredEmail)
    }

    const load = async () => {
      setLoading(true)
      setLoadFailed(false)

      try {
        const result = await getSubscriptionRefundRequestEligibility(api)
        if (!active) {
          return
        }

        setEligibility(result)

        if (!preferredEmail && result.existingRequest?.contactEmail) {
          setContactEmail(result.existingRequest.contactEmail)
        }
      } catch {
        if (!active) {
          return
        }

        setLoadFailed(true)
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      active = false
    }
  }, [api, auth.user])

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return t('notAvailable')
    }

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return t('notAvailable')
    }

    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(parsed)
  }

  const getStatusBadgeVariant = (status?: SubscriptionRefundRequestStatus) => {
    if (status === 'Processed') return 'secondary'
    if (status === 'Rejected') return 'destructive'
    return 'outline'
  }

  const getStatusLabel = (status?: SubscriptionRefundRequestStatus) => {
    if (status === 'Processed') return t('subscriptionRefundStatusProcessed')
    if (status === 'Rejected') return t('subscriptionRefundStatusRejected')
    return t('subscriptionRefundStatusPending')
  }

  const getEligibilityMessage = () => {
    if (!eligibility) {
      return ''
    }

    if (eligibility.status === 'AlreadyRequested') {
      return t('subscriptionRefundExistingRequestMessage')
    }

    if (eligibility.status === 'WindowExpired') {
      return t('subscriptionRefundWindowExpiredMessage')
    }

    if (eligibility.status === 'PaymentNotRefundable') {
      return t('subscriptionRefundPaymentNotRefundableMessage')
    }

    if (eligibility.status === 'NoActiveProSubscription') {
      return t('subscriptionRefundNoActiveProMessage')
    }

    return t('subscriptionRefundEligibleMessage')
  }

  const handleSubmit = async () => {
    setSubmitError(null)

    if (!contactEmail.trim()) {
      setSubmitError(t('subscriptionRefundEmailRequired'))
      return
    }

    if (eligibility?.refundReasonRequired && !reason.trim()) {
      setSubmitError(t('subscriptionRefundReasonRequired'))
      return
    }

    setSubmitting(true)
    try {
      await createSubscriptionRefundRequest(api, {
        contactEmail: contactEmail.trim(),
        reason: reason.trim(),
      })

      const refreshed = await getSubscriptionRefundRequestEligibility(api)
      setEligibility(refreshed)
      setReason('')
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : t('subscriptionRefundSubmitError')
      setSubmitError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const safeAreaTop = 'var(--ion-safe-area-top, env(safe-area-inset-top))'
  const safeAreaBottom = 'var(--ion-safe-area-bottom, env(safe-area-inset-bottom))'
  const policyUrl = eligibility?.publicPolicyUrl || publicLegalConfiguration.legalPages.cancellationAndRefunds
  const officialContactEmail = eligibility?.officialContactEmail || publicLegalConfiguration.contacts.supportEmail
  const officialContactHref = `mailto:${officialContactEmail}`
  const refundAccessNotice = publicLegalConfiguration.lifecycle.refundCancellationEndsProImmediately
    ? t('subscriptionRefundAccessEndsImmediately')
    : t('subscriptionRefundAccessFollowsPolicy')

  return (
    <IonPage>
      <IonContent fullscreen className="bg-gradient-to-br from-slate-50 via-stone-100 to-amber-50">
        <div
          className="min-h-screen"
          style={{
            minHeight: '100dvh',
            paddingBottom: `max(0.75rem, ${safeAreaBottom})`,
          }}
        >
          <div
            className="sticky top-0 z-30 border-b border-stone-200 bg-white/90 backdrop-blur-sm"
            style={{
              paddingTop: `max(0.5rem, ${safeAreaTop})`,
            }}
          >
            <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-action-700">
                  {t('subscriptionRefundEyebrow')}
                </p>
                <h1 className="truncate text-lg font-semibold text-slate-900 md:text-2xl">
                  {t('subscriptionRefundTitle')}
                </h1>
                <p className="mt-1 hidden text-sm text-slate-600 sm:block">
                  {t('subscriptionRefundSubtitle')}
                </p>
              </div>
              <Button
                variant="outline"
                className="border-stone-300 bg-white/80 text-slate-700 hover:bg-stone-50"
                onClick={() => history.goBack()}
              >
                <ArrowLeft className="h-4 w-4" />
                {t('goBack')}
              </Button>
            </div>
          </div>

          <main className="mx-auto max-w-5xl px-4 py-5 md:py-8">
            <div className="mb-4 rounded-2xl border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-sky-900 shadow-sm">
              {t('subscriptionRefundIntro')}
            </div>

            {loading ? (
              <Card className="border-stone-200 bg-white/95">
                <CardContent className="py-8 text-sm text-slate-600">{t('loading')}</CardContent>
              </Card>
            ) : loadFailed || !eligibility ? (
              <Card className="border-red-200 bg-white/95">
                <CardContent className="py-8 text-sm text-red-600">{t('subscriptionRefundLoadError')}</CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
                <Card className="border-stone-200 bg-white/95 shadow-sm">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <ShieldAlert className="h-5 w-5 text-amber-500" />
                      {t('subscriptionRefundEligibilityTitle')}
                    </CardTitle>
                    <CardDescription>{getEligibilityMessage()}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={eligibility.canSubmit ? 'secondary' : 'outline'}>
                        {eligibility.canSubmit
                          ? t('subscriptionRefundEligibleBadge')
                          : t('subscriptionRefundUnavailableBadge')}
                      </Badge>
                      {eligibility.existingRequest?.status ? (
                        <Badge variant={getStatusBadgeVariant(eligibility.existingRequest.status)}>
                          {getStatusLabel(eligibility.existingRequest.status)}
                        </Badge>
                      ) : null}
                    </div>

                    <div className="grid gap-3 rounded-2xl border border-stone-200 bg-stone-50/80 p-4 sm:grid-cols-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {t('subscriptionRefundPlanLabel')}
                        </p>
                        <p className="mt-1 text-sm text-slate-800">{eligibility.planName || t('notAvailable')}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {t('subscriptionRefundEligibleUntilLabel')}
                        </p>
                        <p className="mt-1 text-sm text-slate-800">{formatDateTime(eligibility.eligibleUntilUtc)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {t('subscriptionRefundSubscribedAtLabel')}
                        </p>
                        <p className="mt-1 text-sm text-slate-800">{formatDateTime(eligibility.startsAtUtc)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {t('subscriptionRefundWindowLabel')}
                        </p>
                        <p className="mt-1 flex items-center gap-2 text-sm text-slate-800">
                          <Clock3 className="h-4 w-4 text-action-500" />
                          {t('subscriptionRefundWindowValue', { count: eligibility.eligibilityWindowDays })}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-sky-200 bg-sky-50/80 p-4 text-sm text-sky-900">
                      <p>{t('subscriptionRefundAssessmentNotice')}</p>
                      <p className="mt-2">{refundAccessNotice}</p>
                      <div className="mt-3 flex flex-wrap gap-3">
                        <a
                          className="inline-flex items-center gap-2 font-medium text-sky-800 underline-offset-4 hover:underline"
                          href={policyUrl}
                          {...getSafeLinkAttributes(policyUrl, 'legal')}
                        >
                          {t('subscriptionRefundPolicyLink')}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <a
                          className="inline-flex items-center gap-2 font-medium text-sky-800 underline-offset-4 hover:underline"
                          href={officialContactHref}
                        >
                          {officialContactEmail}
                        </a>
                      </div>
                    </div>

                    {eligibility.existingRequest ? (
                      <div className="rounded-2xl border border-stone-200 bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <h2 className="text-sm font-semibold text-slate-900">
                            {t('subscriptionRefundExistingRequestTitle')}
                          </h2>
                          <Badge variant={getStatusBadgeVariant(eligibility.existingRequest.status)}>
                            {getStatusLabel(eligibility.existingRequest.status)}
                          </Badge>
                        </div>
                        <p className="mt-3 text-sm text-slate-600">
                          {t('subscriptionRefundSubmittedAtLabel')}: {formatDateTime(eligibility.existingRequest.createdAtUtc)}
                        </p>
                        <div className="mt-3 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-slate-700">
                          {eligibility.existingRequest.reason}
                        </div>
                        {eligibility.existingRequest.adminNotes ? (
                          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            {eligibility.existingRequest.adminNotes}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>

                <div className="space-y-4">
                  <Card className="border-sky-200 bg-white/95 shadow-sm">
                    <CardHeader className="gap-3">
                      <CardTitle className="text-slate-900">{t('subscriptionRefundContactTitle')}</CardTitle>
                      <CardDescription>{t('subscriptionRefundContactDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <SupportContactLinks />
                    </CardContent>
                  </Card>

                  <Card className="border-stone-200 bg-white/95 shadow-sm">
                    <CardHeader className="gap-3">
                      <CardTitle className="flex items-center gap-2 text-slate-900">
                        <MessageSquare className="h-5 w-5 text-action-500" />
                        {t('subscriptionRefundFormTitle')}
                      </CardTitle>
                      <CardDescription>{t('subscriptionRefundFormDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-800" htmlFor="refund-contact-email">
                          {t('subscriptionRefundContactEmailLabel')}
                        </label>
                        <Input
                          id="refund-contact-email"
                          type="email"
                          value={contactEmail}
                          onChange={(event) => setContactEmail(event.target.value)}
                          placeholder={t('subscriptionRefundContactEmailPlaceholder')}
                          disabled={!eligibility.canSubmit || submitting}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-800" htmlFor="refund-reason">
                          {eligibility.refundReasonRequired
                            ? t('subscriptionRefundReasonLabel')
                            : t('subscriptionRefundMessageLabel')}
                        </label>
                        <textarea
                          id="refund-reason"
                          className="min-h-[150px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                          placeholder={t('subscriptionRefundReasonPlaceholder')}
                          disabled={!eligibility.canSubmit || submitting}
                        />
                      </div>

                      {submitError ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                          {submitError}
                        </div>
                      ) : null}

                      <Button
                        className="w-full bg-slate-900 text-white hover:bg-slate-800"
                        disabled={!eligibility.canSubmit || submitting}
                        onClick={handleSubmit}
                      >
                        {submitting ? t('subscriptionRefundSubmitting') : t('subscriptionRefundSubmit')}
                      </Button>

                      {!eligibility.canSubmit ? (
                        <p className="text-xs text-slate-500">{getEligibilityMessage()}</p>
                      ) : null}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </main>
        </div>
      </IonContent>
    </IonPage>
  )
}

export default SubscriptionRefundRequestPage
